import { Modal as ModalModule, UI, Utils } from 'common';
import React, { useContext, useState } from 'react';
import PlanUpdatedSuccessModal from './PlanUpdatedSuccessModal.component';
import { isInTrialBillingPeriod } from './planRules';

const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { Spinner } = UI;
const { BillingPlans, Communication, Session, Util } = Utils;

interface LegacyPlanChangeConfirmationModalData {
	newPlanName: string;
	newPlanId: number;
	newPlanPrice: number;
}

interface LegacyPlanChangeConfirmationModalProps {
	modalId: string;
	data: LegacyPlanChangeConfirmationModalData;
}

const LegacyPlanChangeConfirmationModal = ({
	modalId,
	data,
}: LegacyPlanChangeConfirmationModalProps) => {
	const { newPlanName, newPlanId, newPlanPrice } = data;
	const { closeModal, showModal, showError } = useContext(ModalContext) as any;
	const [spinner, setSpinner] = useState(false);

	const { BillingDayOfMonth } = (Session as any).getInternalAccountInfo() ?? {};
	const billingDate = BillingDayOfMonth
		? (Util as any).getBillingDate(BillingDayOfMonth)
		: '';
	const inTrialBilling = isInTrialBillingPeriod();
	const { nextBillingDate } = (Session.getAccountInfo() as any) ?? {};
	const trialEndDate = nextBillingDate
		? (Util as any).formatDateReadable(nextBillingDate)
		: '';

	const onChangePlan = async () => {
		setSpinner(true);
		try {
			const response = await (Communication as any).changePlan({
				PlanId: newPlanId,
				PromoCode: null,
				Disposition: {},
				CancelReason: {},
				newPlanPrice,
			});

			if ((Communication as any).isSuccessful(response)) {
				await Promise.all([Session.loadAccountInfo(), BillingPlans.init()]);
				setSpinner(false);
				closeModal(modalId, ModalButtonActionType.Ok);
				showModal({ component: PlanUpdatedSuccessModal });
			} else {
				setSpinner(false);
				showError({ response });
			}
		} catch (error) {
			setSpinner(false);
			showError({ response: { message: (error as Error).message } });
		}
	};

	const primaryButton = {
		type: ModalButtonActionType.Confirm,
		text: 'Confirm',
		onClick: onChangePlan,
		dataqa: 'legacy-plan-change-confirm',
	};

	const secondaryButton = {
		type: ModalButtonActionType.Cancel,
		text: 'Cancel',
		buttonType: 'tertiary-ghost',
		onClick: () => closeModal(modalId, ModalButtonActionType.Cancel),
		dataqa: 'legacy-plan-change-cancel',
	};

	return (
		<Modal
			modalId={modalId}
			className="subscription-plan plan-change-modal"
			header={`Change Plan to ${BillingPlans.prettyPrintPlan(newPlanName)}`}
			primaryButton={primaryButton}
			secondaryButton={secondaryButton}
		>
			<div className="body">
				{inTrialBilling ? (
					<>
						<p>
							Once confirmed, you will have access to your new plan features in
							just a few minutes.
						</p>
						<br />
						<p>
							Your subscription will automatically renew monthly starting{' '}
							{trialEndDate} at ${newPlanPrice.toFixed(2)}, including your first
							month fee. You can cancel anytime before then at no charge.
						</p>
					</>
				) : (
					<>
						<p>
							Once confirmed, you will have access to your new plan features in
							just a few minutes.
						</p>
						<br />
						<p>
							Your invoice for this billing cycle
							{billingDate ? ` on ${billingDate}` : ''} will include a prorated
							amount for your new plan, based on the number of days remaining in
							the billing cycle.
						</p>
					</>
				)}
			</div>
			{spinner && <Spinner msg="Changing plan..." />}
		</Modal>
	);
};

export default LegacyPlanChangeConfirmationModal;
